import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Folder, Search, Download, Trash2, Upload as UploadIcon, FileText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const FOLDERS = [
  { id: "company_manuals", label: "Company Manuals" },
  { id: "project_documents", label: "Project Documents" },
  { id: "safety_documents", label: "Safety Documents" },
  { id: "forms_templates", label: "Forms & Templates" },
  { id: "technical_references", label: "Technical References" },
];

export default function Documents() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", category: "company_manuals", description: "", project_id: "",
    assigned_to_all: true, assigned_user_ids: [], file: null,
  });

  const reload = async () => {
    const params = {};
    if (category) params.category = category;
    if (search) params.search = search;
    const [d, u] = await Promise.all([
      api.get("/documents", { params }),
      user.role === "admin" ? api.get("/users") : Promise.resolve({ data: [] }),
    ]);
    setDocs(d.data); setEmployees(u.data);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [category, search]);

  const upload = async () => {
    if (!form.file || !form.title) return toast.error("Title and file required");
    const fd = new FormData();
    fd.append("file", form.file);
    fd.append("title", form.title);
    fd.append("category", form.category);
    if (form.description) fd.append("description", form.description);
    if (form.project_id) fd.append("project_id", form.project_id);
    fd.append("assigned_to_all", form.assigned_to_all);
    if (!form.assigned_to_all) fd.append("assigned_user_ids", form.assigned_user_ids.join(","));
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); setOpen(false);
      setForm({ title: "", category: "company_manuals", description: "", project_id: "", assigned_to_all: true, assigned_user_ids: [], file: null });
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const download = async (id, name) => {
    const r = await api.get(`/documents/${id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/documents/${id}`); reload(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">DOCUMENT STORAGE</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight mt-1">Library</h1>
        </div>
        {user.role === "admin" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-doc-btn" className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase">
                <UploadIcon className="h-4 w-4 mr-2" /> Upload Document
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-[#222] rounded-none text-white max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading uppercase">New Document</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="label-eyebrow">TITLE</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono" /></div>
                <div>
                  <Label className="label-eyebrow">CATEGORY</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                      {FOLDERS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="label-eyebrow">DESCRIPTION</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-black border-[#222] rounded-none mt-2 font-mono text-sm" rows={2} /></div>
                <div><Label className="label-eyebrow">FILE</Label><Input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] })} className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono text-xs" /></div>
                <label className="flex items-center gap-3 border border-[#222] p-3">
                  <Checkbox checked={form.assigned_to_all} onCheckedChange={(v) => setForm({ ...form, assigned_to_all: !!v })} />
                  <span className="text-sm">Visible to ALL employees</span>
                </label>
                {!form.assigned_to_all && (
                  <div>
                    <Label className="label-eyebrow">ASSIGN TO</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto border border-[#222] p-3">
                      {employees.filter((e) => e.role === "employee").map((e) => (
                        <label key={e.id} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={form.assigned_user_ids.includes(e.id)}
                            onChange={() => {
                              const list = form.assigned_user_ids;
                              setForm({ ...form, assigned_user_ids: list.includes(e.id) ? list.filter((x) => x !== e.id) : [...list, e.id] });
                            }}
                            className="accent-white"
                          />
                          <span className="truncate">{e.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter><Button onClick={upload} className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase">Upload</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Folder tree */}
        <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c]">
          <div className="p-4 border-b border-[#1c1c1c] label-eyebrow">FOLDERS</div>
          <ul className="p-2">
            <li>
              <button
                onClick={() => setCategory("")}
                data-testid="folder-all"
                className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm border-l-2 ${category === "" ? "border-white bg-[#101010]" : "border-transparent hover:bg-[#0d0d0d]"}`}
              >
                <Folder className="h-4 w-4" /> All Documents
              </button>
            </li>
            {FOLDERS.map((f) => (
              <li key={f.id}>
                <button
                  onClick={() => setCategory(f.id)}
                  data-testid={`folder-${f.id}`}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm border-l-2 ${category === f.id ? "border-white bg-[#101010]" : "border-transparent hover:bg-[#0d0d0d] text-[#aaa]"}`}
                >
                  <Folder className="h-4 w-4" /> {f.label}
                </button>
              </li>
            ))}
          </ul>
        </Card>

        {/* File grid */}
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="doc-search"
              placeholder="Search documents…"
              className="pl-10 bg-black border-[#222] rounded-none h-11 font-mono"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="docs-grid">
            {docs.map((d) => (
              <Card key={d.id} className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-4 flex flex-col gap-2">
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-[#666] shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{d.title}</div>
                    <div className="font-mono text-xs text-[#666] truncate">{d.original_filename}</div>
                  </div>
                </div>
                <div className="label-eyebrow">{FOLDERS.find((f) => f.id === d.category)?.label || d.category}</div>
                {d.description && <p className="text-xs text-[#999] line-clamp-2">{d.description}</p>}
                <div className="mt-auto pt-3 border-t border-[#1c1c1c] flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => download(d.id, d.original_filename)} className="rounded-none border border-[#222] text-xs"><Download className="h-3 w-3 mr-1" /> Open</Button>
                  {user.role === "admin" && <Button size="sm" variant="ghost" onClick={() => remove(d.id)} className="rounded-none border border-[#222] text-xs text-red-400"><Trash2 className="h-3 w-3" /></Button>}
                </div>
              </Card>
            ))}
            {docs.length === 0 && (
              <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-10 sm:col-span-2 xl:col-span-3 text-center">
                <div className="label-eyebrow">EMPTY FOLDER</div>
                <div className="text-sm text-[#888] mt-2">No documents in this category yet.</div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
